import { memo, type MouseEvent as ReactMouseEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibSparklesIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import { ChatMode, useAgentChat } from './AgentChatContext'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './AgentButton.module.scss'

export interface AgentButtonProps {
  className?: string
  isFloating?: boolean
  isMenuExpanded?: boolean
  onClick?: (event: ReactMouseEvent) => void
}

/**
 * Button component for opening the AI Assistant chat.
 *
 * Supports two display modes:
 * - **Menu mode** (`isMenuExpanded`): Used in the left rail menu. When expanded, shows icon + text.
 *   When collapsed (isMenuExpanded=false), shows icon-only.
 * - **Floating mode** (`isFloating`): Displays as a floating button over modal overlays.
 *   Always shows as icon-only and automatically switches chat to RAIL mode when opened.
 *
 * @param isFloating - When true, renders as a floating button and automatically sets chat mode to RAIL when opened
 * @param isMenuExpanded - Controls expanded (icon + text) vs collapsed (icon-only) appearance in menu mode
 * @param onClick - Optional click handler that fires before the default chat opening behavior
 */
const AgentButton = memo(
  ({
    className,
    isFloating = false,
    isMenuExpanded,
    onClick,
  }: AgentButtonProps) => {
    const { t } = useTranslation(['Agent'])
    const { openChat, setChatMode } = useAgentChat()

    const handleClick = useCallback(
      (event: ReactMouseEvent) => {
        event.stopPropagation()
        event.preventDefault()
        if (onClick) {
          onClick(event)
        }
        if (isFloating) {
          setChatMode(ChatMode.RAIL)
        }
        openChat()
      },
      [onClick, isFloating, setChatMode, openChat],
    )

    const isCollapsed = isMenuExpanded === false || isFloating

    return (
      <KibButtonNew
        aria-label={t('Agent:AI_ASSISTANT')}
        className={classNames(
          styles.button,
          {
            [styles.closed]: isCollapsed,
            [styles.expanded]: !isCollapsed,
            [styles.floating]: isFloating,
            [styles.menuItem]: !isFloating,
          },
          className,
        )}
        data-menu-expanded={isMenuExpanded}
        icon={<KibSparklesIcon height={32} width={32} />}
        iconOnly={isCollapsed}
        onClick={handleClick}
      >
        {!isCollapsed && (
          <span className={styles.text}>{t('Agent:AI_ASSISTANT')}</span>
        )}
      </KibButtonNew>
    )
  },
)

AgentButton.displayName = 'AgentButton'

export default AgentButton
